<template>
    <Card
        v-if="meta && typeof meta.total === 'number'"
        :padding="0"
        custom-classes="tw:px-4 tw:py-3"
    >
        <div class="tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:items-center tw:md:justify-between">
            <!-- Izquierda: elementos por página. En móvil va en COLUMNA (el
                 selector y el resumen de página no deben compartir línea);
                 desde md vuelven a la fila, alineados a la izquierda. -->
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:md:flex-row">
                <TableElementsPerPage
                    :value="model.per_page || 5"
                    @input="onPerPageChange"
                />

                <!-- Resumen de página, solo en móvil -->
                <span class="tw:text-sm tw:text-gray-500 tw:whitespace-nowrap tw:md:hidden">
                    Página {{ model.page || 1 }} de {{ totalPaginas || 1 }}
                </span>
            </div>

            <!-- Centro: rango mostrado. Ocupa el espacio libre para que, cuando
                 no hay paginador (una sola página), el texto quede pegado al
                 borde derecho en vez de flotando como si estuviera centrado. -->
            <div
                class="tw:text-center tw:text-sm tw:text-gray-500 tw:md:flex-1"
                :class="totalPaginas > 1 ? '' : 'tw:md:text-right'"
                data-testid="table-footer-range"
            >
                Mostrando {{ desde }} - {{ hasta }} de {{ meta.total || 0 }} resultados
            </div>

            <!-- Derecha: paginación -->
            <div class="tw:flex tw:items-center tw:justify-center tw:md:justify-end">
                <TablePagination
                    :value="model.page || 1"
                    :total="meta.total || 0"
                    :per-page="model.per_page || 5"
                    @input="onPageChange"
                />
            </div>
        </div>
    </Card>
</template>

<script setup>
    import TableElementsPerPage from '@/modules/admin/ui/table/TableElementsPerPage.vue'
    import TablePagination from '@/modules/admin/ui/table/TablePagination.vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import { computed } from 'vue'

    const props = defineProps({
        value: { type: Object, required: true },
        meta: { type: Object, default: () => ({}) }
    })

    const emit = defineEmits(['input'])

    /**
     * El modelo llega como prop y NUNCA se muta en sitio. `props.value` es el
     * objeto del consumidor (a veces el resultado de un `computed`), así que
     * escribir en `model.page` / `model.per_page` mutaba una copia y no
     * emitía `input`: la paginación no respondía. Cada cambio emite un objeto
     * nuevo con las claves actualizadas.
     */
    const model = computed(() => props.value || {})

    function emitirCambio(overrides) {
        emit('input', { ...model.value, ...overrides })
    }

    function onPageChange(page) {
        emitirCambio({ page })
    }

    function onPerPageChange(perPage) {
        const parsed = parseInt(perPage, 10)
        const nextPerPage = Number.isNaN(parsed) ? (model.value.per_page || 5) : parsed
        // Al cambiar el tamaño de página se vuelve a la primera.
        const overrides = { per_page: nextPerPage }
        if (nextPerPage !== model.value.per_page) overrides.page = 1
        emitirCambio(overrides)
    }

    const desde = computed(() => ((model.value.page || 1) - 1) * (model.value.per_page || 5) + 1)
    const hasta = computed(() => Math.min((model.value.page || 1) * (model.value.per_page || 5), props.meta.total || 0))
    const totalPaginas = computed(() => Math.ceil((props.meta.total || 0) / (model.value.per_page || 5)))
</script>
